import React, { useState, useEffect, useRef } from 'react';
import StepLayout from '../components/StepLayout';
import SelectionCard from '../components/SelectionCard';
import { api } from '../api';

// iter-26: rebuild per Source Attribution Page UI Spec.
//   Heading       — "Where did you hear about us? *" (no emoji)
//   Cards         — single-select via SelectionCard (orange pattern)
//   Display label / backend value (values are STABLE — attribution data +
//   trafficAttribution.ts CHANNEL_CASE + rowToAccount key off them exactly):
//     "Facebook ad"    → "Facebook Paid Advertisement"  (→ 'Meta' bucket)
//     "TikTok ad"      → "TikTok Paid Advertisement"     (→ 'TikTok' bucket)
//     "Google search"  → "Google Search"                 (→ 'Google' bucket)
//     "Other"          → "Other"                          (→ 'Other' bucket)
//   Auto-advance  — picks fire handleNext after 250ms (iter-24 carry-over)
//   Trustpilot    — kept (StepLayout default mount)
//
// B-REFERRAL-LINK-ONLY (2026-07-10): the "Referral" self-report card was REMOVED
// (referral payouts are LINK-ONLY via ?ref=<code>, captured in
// utils/referralAttribution.js; self-declaring a referral here produced
// wrong/ambiguous attribution). This is purely a MARKETING-SOURCE question now.
// B-SOURCE-OPTIONS (2026-07-10): added TikTok / Google / Other so a non-Facebook
// seller has an honest choice (a lone "Facebook ad" forced everyone to mislabel).
// Any NEW value here must also get a bucket in vantage-api trafficAttribution.ts
// (CHANNEL_CASE + CANONICAL_CHANNELS + acquisitionCostMinor) or it silently folds
// into 'Paid - source unknown (no utm captured)'. Routing is source-agnostic: every pick jumps via
// goToStep(19) (the proven Facebook fast-path — skips the Finalising/Cleanup wait
// screens), so all sources take the identical, byte-tested funnel tail.
const OPTIONS = [
  { value: 'Facebook Paid Advertisement', label: 'Facebook ad' },
  { value: 'TikTok Paid Advertisement', label: 'TikTok ad' },
  { value: 'Google Search', label: 'Google search' },
  { value: 'Other', label: 'Other' },
];

export default function Step15ReferralSource({ sessionId, sessionData, updateData, onNext, goToStep }) {
  const [source, setSource] = useState(sessionData.referralSource || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const advancedRef = useRef(false);

  const handleNext = async (sourceOverride) => {
    const value = sourceOverride || source;
    if (!value) {
      setError('Please select an option.');
      return;
    }
    if (advancedRef.current) return;
    advancedRef.current = true;
    setError('');
    setLoading(true);
    setSubmitError('');
    try {
      await api.updateReferral(sessionId, { referralSource: value });
      updateData({ referralSource: value });
      // Source-agnostic routing: every marketing source takes the proven
      // fast-path — jump past ReferrerName + Finalising/Cleanup wait screens to
      // FinalDetails (iter-26 position 19). onNext() is a fallback only if
      // goToStep isn't wired (defensive; never expected in the live flow).
      if (goToStep) {
        goToStep(19);
      } else {
        onNext();
      }
    } catch (err) {
      advancedRef.current = false;
      setSubmitError(err.message || 'Failed to save.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!source || advancedRef.current) return;
    const t = setTimeout(() => {
      handleNext(source);
    }, 250);
    return () => clearTimeout(t);
  }, [source]);

  const canSubmit = !!source && !loading;

  return (
    <StepLayout title="Where did you hear about us? *">
      <div
        role="radiogroup"
        aria-label="How did you hear about us"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 32,
        }}
      >
        {OPTIONS.map((opt) => (
          <SelectionCard
            key={opt.value}
            id={`referral-${opt.value.replace(/\s+/g, '-').toLowerCase()}`}
            name="referralSource"
            value={opt.value}
            label={opt.label}
            selected={source === opt.value}
            onSelect={(v) => { setSource(v); if (error) setError(''); }}
            disabled={loading}
          />
        ))}
      </div>

      {error && <p className="form-field__error">{error}</p>}
      {submitError && <p className="form-field__error">{submitError}</p>}

      <div className="cta-row">
        <button
          type="button"
          onClick={() => handleNext()}
          disabled={!canSubmit}
          aria-disabled={!canSubmit}
          className="cta-row__btn primary-cta"
        >
          {loading ? 'Saving...' : 'Next'}
        </button>
      </div>
    </StepLayout>
  );
}
